import { useState } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { BookOpenText, ArrowRight, Star, CaretDown } from "@phosphor-icons/react";
import { TOOLS, PILLARS, PILLAR_ORDER } from "@/lib/tools";
import { TOOL_GUIDES } from "@/data/toolGuides";

export default function GuideIndex() {
  const [openPillars, setOpenPillars] = useState({});
  const byPillar = PILLAR_ORDER.reduce((acc, p) => {
    acc[p] = Object.values(TOOLS).filter((t) => t.pillar === p);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="max-w-5xl mx-auto w-full py-10 px-6 lg:px-10">
        <div className="flex items-center gap-2 text-slate-500">
          <BookOpenText size={18} weight="duotone" />
          <p className="text-xs font-semibold tracking-[0.12em] uppercase">How &amp; Why</p>
        </div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">The Method Library</h1>
        <p className="mt-2 text-slate-500 max-w-2xl">
          The thinking behind every tool — what it is, when to reach for it, and how the steps work. Start with the
          FOCUS·ARMOR deep-dive, or open any framework below.
        </p>

        {/* FOCUS·ARMOR featured deep-dive */}
        <Link
          to="/guide/focus-armor"
          data-testid="guide-card-focus-armor"
          className="group mt-8 flex items-center justify-between gap-4 bg-slate-900 text-white rounded-sm p-6 hover:bg-slate-800 transition-colors"
        >
          <div>
            <div className="flex items-center gap-2">
              <Star size={16} weight="fill" className="text-amber-400" />
              <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase text-amber-300">Featured · Full Deep-Dive</span>
            </div>
            <h2 className="mt-2 text-xl font-semibold">FOCUS·ARMOR — the complete method</h2>
            <p className="mt-1 text-sm text-slate-300 leading-relaxed max-w-2xl">The full origin story, the two phases, the ten steps and the gatekeeper checks behind the flagship decision-and-execution framework.</p>
          </div>
          <ArrowRight size={20} className="shrink-0 group-hover:translate-x-1 transition-transform" />
        </Link>

        <div className="mt-10 space-y-4" data-testid="guide-library">
          {PILLAR_ORDER.map((pkey) => {
            const pillar = PILLARS[pkey];
            const tools = byPillar[pkey].filter((t) => t.type !== "focus-armor" && TOOL_GUIDES[t.type]);
            if (!tools.length) return null;
            const isOpen = !!openPillars[pkey];
            return (
              <section key={pkey} data-testid={`guide-pillar-${pkey.toLowerCase()}`} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <button
                  data-testid={`guide-pillar-toggle-${pkey.toLowerCase()}`}
                  onClick={() => setOpenPillars((p) => ({ ...p, [pkey]: !p[pkey] }))}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${pillar.dot}`} />
                    <h2 className={`font-mono text-sm font-bold uppercase tracking-[0.1em] ${pillar.text}`}>{pillar.label}</h2>
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold shrink-0 ${pillar.chip}`}>{tools.length} methods</span>
                  </div>
                  <CaretDown size={18} className={`text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 pt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 border-t border-slate-100">
                    {tools.map((t) => (
                      <Link
                        key={t.type}
                        to={`/guide/${t.type}`}
                        data-testid={`guide-card-${t.type}`}
                        className={`group bg-white border border-slate-200 rounded-sm p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${pillar.ring}`}
                      >
                        <div className={`w-11 h-11 rounded-sm flex items-center justify-center ${pillar.chip}`}>
                          <t.icon size={22} weight="duotone" />
                        </div>
                        <h3 className="mt-4 text-base font-semibold text-slate-900">{t.name}</h3>
                        <p className="mt-1 text-sm text-slate-500 leading-relaxed">{t.tagline}</p>
                        <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 group-hover:gap-2.5 transition-all">
                          Read the method <ArrowRight size={14} />
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
